/**
 * Tile-mode gate (national-scale contract, CLIENT section).
 *
 * The trail manifest MAY carry a `tiles` block once the national bake
 * has published PMTiles archives to the beta PWA bucket:
 *
 *   tiles: {
 *     version: "<bake version>",
 *     observations_url: "https://…/tiles/v<version>/observations.pmtiles",
 *     watersheds_url:   "https://…/tiles/v<version>/watersheds.pmtiles",
 *   }
 *
 * PRESENCE of a valid `tiles` block is the ONE signal that switches
 * /explore into MapLibre tile mode; absence (or any malformed block)
 * falls back to the legacy Leaflet + GeoJSON path so old manifests keep
 * working during rollout. No other flag, env var, or URL param flips
 * the mode — keep it that way so the two paths can't disagree.
 */

export interface TrailTilesConfig {
  version: string;
  observations_url: string;
  watersheds_url: string;
  /**
   * Ladder-era additions (zoom-ladder increment 1, LOCKED cross-repo
   * contract). ALL optional and parsed LENIENTLY: a malformed extra
   * must never knock the app back to Leaflet — asymmetric with the
   * strict URL rule above, deliberately.
   */
  /** ≥2 declares ladder-era tiles (watershed_points + impairment_class). */
  schema_version?: number;
  /** basins.pmtiles archive URL — absence = no basin rung (never legacy fallback). */
  basins_url?: string;
  /** Raw bbox [w, s, e, n] of scanned observations — data-aware landing. */
  data_bounds?: [number, number, number, number];
  /**
   * Places surface (schema 4). Two archives, not one, because they
   * carry different licenses: parks are USGS PAD-US (public domain),
   * paths are OpenStreetMap (ODbL). Keeping them separable is what lets
   * the ODbL layer be a basemap reference without contaminating the
   * CC BY-SA commons export. Absence = no park/trail rung, never a
   * legacy fallback — same rule as basins_url.
   */
  parks_url?: string;
  trails_url?: string;
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/**
 * True for URL shapes the pmtiles protocol can actually fetch. http(s)
 * absolute URLs are what the bake publishes (absolute CloudFront URLs
 * per the manifest contract); root-relative paths are accepted so the
 * e2e fixtures / a same-origin bucket layout also work.
 */
function isFetchableUrl(v: unknown): boolean {
  if (!isNonEmptyString(v)) return false;
  return /^https?:\/\//.test(v) || v.startsWith('/');
}

/**
 * THE tile-mode gate. Returns the validated tiles config when the
 * manifest carries a usable `tiles` block, else null (legacy mode).
 * Deliberately strict: a partially-formed block (missing/empty URL,
 * missing version) is treated as "no tiles" rather than half-enabling
 * tile mode against broken URLs.
 */
export function tilesConfigOf(manifest: unknown): TrailTilesConfig | null {
  if (!manifest || typeof manifest !== 'object') return null;
  const tiles = (manifest as { tiles?: unknown }).tiles;
  if (!tiles || typeof tiles !== 'object') return null;
  const t = tiles as Record<string, unknown>;
  // Version may arrive as a number from older bake scripts — normalize.
  const version =
    isNonEmptyString(t.version)
      ? t.version
      : typeof t.version === 'number' && Number.isFinite(t.version)
        ? String(t.version)
        : null;
  if (version === null) return null;
  if (!isFetchableUrl(t.observations_url) || !isFetchableUrl(t.watersheds_url)) {
    return null;
  }
  // Ladder-era optional extras (LOCKED contract): parsed leniently AFTER
  // the strict checks above. A malformed schema_version / basins_url /
  // data_bounds is silently DROPPED (the block survives) — never null
  // the whole config, never throw.
  const schemaVersion =
    typeof t.schema_version === 'number' &&
    Number.isFinite(t.schema_version) &&
    t.schema_version > 0
      ? t.schema_version
      : null;
  const basinsUrl = isFetchableUrl(t.basins_url) ? (t.basins_url as string) : null;
  const dataBounds = parseDataBounds(t.data_bounds);
  const parksUrl = isFetchableUrl(t.parks_url) ? (t.parks_url as string) : null;
  const trailsUrl = isFetchableUrl(t.trails_url) ? (t.trails_url as string) : null;
  return {
    version,
    observations_url: t.observations_url as string,
    watersheds_url: t.watersheds_url as string,
    // Conditional spread keeps absent keys ABSENT (not undefined-assigned)
    // so pre-ladder toEqual assertions on the 3-field shape stay exact.
    ...(schemaVersion !== null ? { schema_version: schemaVersion } : {}),
    ...(basinsUrl !== null ? { basins_url: basinsUrl } : {}),
    ...(dataBounds !== null ? { data_bounds: dataBounds } : {}),
    ...(parksUrl !== null ? { parks_url: parksUrl } : {}),
    ...(trailsUrl !== null ? { trails_url: trailsUrl } : {}),
  };
}

/**
 * Lenient data_bounds parse: a 4-length array of finite numbers with
 * w<e, s<n inside [-180,180]/[-90,90]. Anything else → null (dropped).
 */
function parseDataBounds(v: unknown): [number, number, number, number] | null {
  if (!Array.isArray(v) || v.length !== 4) return null;
  const nums = v.map((x) => (typeof x === 'number' && Number.isFinite(x) ? x : null));
  if (nums.some((x) => x === null)) return null;
  const [w, s, e, n] = nums as number[];
  if (!(w < e && s < n)) return null;
  if (w < -180 || e > 180 || s < -90 || n > 90) return null;
  return [w, s, e, n];
}

/**
 * Data-aware landing classifier: 'regional' iff the bbox spans a
 * multi-state-or-smaller area (≤30° lon × ≤15° lat — CONUS is ~58×25).
 * Regional → fitBounds(data_bounds); national/absent → CONUS fit.
 */
export function landingKindOf(
  db?: [number, number, number, number],
): 'regional' | 'national' {
  if (!db) return 'national';
  const [w, s, e, n] = db;
  return e - w <= 30 && n - s <= 15 ? 'regional' : 'national';
}

/**
 * Cheap CDN warm (issue #87): fire the exact first read the pmtiles
 * client makes (header + root directory = bytes 0–16383) for each
 * archive so the browser HTTP cache serves the Protocol's own request
 * warm. Fire-and-forget; failures are irrelevant (a cache miss merely
 * re-fetches 16 KB). Called as the FIRST line of the tile-mode
 * lifecycle effect, before any await, so the CDN round-trip overlaps
 * the engine-chunk download.
 */
export function warmTileHeaders(cfg: TrailTilesConfig): void {
  const urls = [cfg.observations_url, cfg.watersheds_url];
  if (cfg.basins_url) urls.push(cfg.basins_url);
  for (const u of urls) {
    try {
      fetch(u, { headers: { Range: 'bytes=0-16383' } }).catch(() => {});
    } catch {
      /* fetch unavailable (SSR/test) — warm is best-effort */
    }
  }
}
